import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Copy, Check, Loader2, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPromo = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, available: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [count, setCount] = useState(5);
  const [copied, setCopied] = useState(null);

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/promo/list`);
      setCodes(res.data.codes);
      setStats({ total: res.data.total, used: res.data.used, available: res.data.available });
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Accès réservé à l\'administrateur');
        navigate('/dashboard');
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/promo/generate`, { count });
      toast.success(`${res.data.count} codes générés !`);
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setGenerating(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const copyAllAvailable = () => {
    const available = codes.filter(c => !c.used).map(c => c.code).join('\n');
    navigator.clipboard.writeText(available);
    toast.success(`${codes.filter(c => !c.used).length} codes copiés !`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-stone-600 hover:text-stone-900" data-testid="admin-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-[#FF6B6B]" />
            <span className="font-serif font-semibold text-stone-800">Codes Promo</span>
          </div>
          <div />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 text-center border border-stone-100">
            <p className="text-2xl font-serif font-bold text-stone-900">{stats.total}</p>
            <p className="text-xs text-stone-500">Total</p>
          </div>
          <div className="bg-white rounded-2xl p-4 text-center border border-green-100">
            <p className="text-2xl font-serif font-bold text-green-600">{stats.available}</p>
            <p className="text-xs text-stone-500">Disponibles</p>
          </div>
          <div className="bg-white rounded-2xl p-4 text-center border border-stone-100">
            <p className="text-2xl font-serif font-bold text-stone-400">{stats.used}</p>
            <p className="text-xs text-stone-500">Utilisés</p>
          </div>
        </div>

        {/* Generate */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100 mb-6">
          <h3 className="font-semibold text-stone-900 mb-3">Générer de nouveaux codes</h3>
          <div className="flex gap-3 items-center">
            <input
              type="number"
              min="1"
              max="50"
              value={count}
              onChange={e => setCount(parseInt(e.target.value) || 1)}
              className="w-20 px-3 py-2 border border-stone-200 rounded-xl text-sm text-center"
              data-testid="promo-count-input"
            />
            <Button onClick={handleGenerate} disabled={generating} className="bg-[#FF6B6B] hover:bg-[#EE5253] text-white rounded-xl" data-testid="generate-codes-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Générer
            </Button>
            {stats.available > 0 && (
              <Button onClick={copyAllAvailable} variant="outline" className="rounded-xl ml-auto" data-testid="copy-all-btn">
                <Copy className="w-4 h-4 mr-2" />
                Copier tous les disponibles
              </Button>
            )}
          </div>
        </div>

        {/* Code List */}
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="divide-y divide-stone-50">
            {codes.map(c => (
              <div key={c.code} className={`flex items-center justify-between px-5 py-3 ${c.used ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-sm font-medium ${c.used ? 'text-stone-400 line-through' : 'text-stone-800'}`}>
                    {c.code}
                  </span>
                  {c.used && (
                    <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">utilisé</span>
                  )}
                </div>
                {!c.used && (
                  <button onClick={() => copyCode(c.code)} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid={`copy-${c.code}`}>
                    {copied === c.code ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPromo;
